/**
 * Booking normalisation shared by the list and the detail screen.
 */
import { format, isValid, parseISO } from 'date-fns';
import { attributeMap, parseMaybeJSON } from '@/lib/catalog';
import { cleanLocationText } from '@/utils/cleanLocationText';

export const UPCOMING_STATUSES = ['pending', 'confirmed', 'in_progress', 'payment_pending'];
export const COMPLETED_STATUSES = ['completed'];
export const CANCELLED_STATUSES = [
  'cancelled',
  'partially_cancelled',
  'payment_failed',
  'rejected',
];

// Semantic tokens (tailwind.config.js) so a status reads the same colour on
// every screen; these were raw amber/green/red shades before.
export const STATUS_STYLES = {
  pending: 'bg-warning-tint text-warning ring-warning/20',
  payment_pending: 'bg-warning-tint text-warning ring-warning/20',
  confirmed: 'bg-success-tint text-success ring-success/20',
  in_progress: 'bg-brand-tint text-brand-blueDark ring-brand-secondary/20',
  completed: 'bg-surface-sunken text-ink-700 ring-ink-200',
  cancelled: 'bg-danger-tint text-danger ring-danger/20',
  partially_cancelled: 'bg-danger-tint text-danger ring-danger/20',
  payment_failed: 'bg-danger-tint text-danger ring-danger/20',
  rejected: 'bg-danger-tint text-danger ring-danger/20',
};

export const prettyStatus = (status) =>
  (status || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Which tab a booking belongs in. Status alone isn't enough: organisers rarely
 * mark event bookings "completed", so a confirmed booking for a night that's
 * already over sat under "Upcoming" indefinitely — still offering Cancel. Pass
 * the booking and anything whose slot has ended moves to "completed".
 */
export const bucketOf = (status, booking) => {
  if (COMPLETED_STATUSES.includes(status)) return 'completed';
  if (CANCELLED_STATUSES.includes(status)) return 'cancelled';
  if (booking && isPastBooking(booking)) return 'completed';
  return 'upcoming';
};

/** Cancellable = still an upcoming status *and* the slot hasn't ended. */
export const canCancelBooking = (booking) =>
  UPCOMING_STATUSES.includes(booking?.status) && !isPastBooking(booking);

/**
 * A booking's `dateAndTime`, read as the wall-clock time it was booked for.
 *
 * Both clients send `scheduledTime` as a zoneless "yyyy-MM-dd HH:mm:ss" in
 * IST (the event's own start time); the backend parses it in the server's zone
 * and stores it labelled UTC. So "2026-10-17 19:00:00 +00:00" means 7:00 PM
 * on the 17th — converting it as real UTC showed 12:30 AM on the 18th, the
 * wrong day for every evening event. The digits are the truth; the offset
 * isn't. Falls back to normal parsing for anything not in that shape.
 */
export const parseBookingTime = (value) => {
  if (!value) return null;
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (m) {
    const [, y, mo, d, h, mi] = m.map(Number);
    const wall = new Date(y, mo - 1, d, h, mi);
    return isValid(wall) ? wall : null;
  }
  const iso = parseISO(String(value));
  if (isValid(iso)) return iso;
  const loose = new Date(value);
  return isValid(loose) ? loose : null;
};

// When the booked slot has no duration, treat it as over this long after it
// starts — an event in progress should still read as upcoming.
const DEFAULT_SLOT_MIN = 180;

/** True once the booked slot has ended. Unknown dates are never "past". */
export const isPastBooking = (booking) => {
  const start = parseBookingTime(booking?.date);
  if (!start) return false;
  const end = start.getTime() + (booking?.durationMin || DEFAULT_SLOT_MIN) * 60000;
  return end < Date.now();
};

export const formatWhen = (dateStr, timeStr) => {
  if (!dateStr) return timeStr || 'Date TBA';
  const date = parseBookingTime(dateStr);
  if (!date) return timeStr || String(dateStr);
  return `${format(date, 'EEE, d MMM yyyy')}${timeStr ? ` · ${timeStr}` : `, ${format(date, 'p')}`}`;
};

/**
 * Flatten one `/bookings` row. Tolerant of missing joins — the API returns
 * different depths of nesting depending on the endpoint.
 */
export const normalizeBooking = (booking) => {
  const attrs = attributeMap(booking?.attributes);
  const location = parseMaybeJSON(attrs.event_location);
  const service = booking?.services?.[0]?.service;
  const provider = booking?.provider || service?.provider;

  const lines = (booking?.services || []).flatMap((row) =>
    (row?.variants || []).map((variant) => ({
      id: variant?.id ?? variant?.variantId,
      name: variant?.name || variant?.variant?.name || row?.service?.name || 'Ticket',
      quantity: Number(variant?.qty ?? variant?.quantity ?? 1),
      price: Number(variant?.price ?? variant?.unitPrice ?? 0),
      seatLabels: variant?.seatLabels || [],
    }))
  );

  const ticketCount =
    lines.reduce((sum, line) => sum + line.quantity, 0) ||
    Number(booking?.totalQty ?? booking?.quantity ?? 0);

  return {
    id: booking?.id,
    code: booking?.bookingCode || booking?.code || null,
    status: booking?.status,
    paymentStatus: booking?.paymentStatus || null,
    title: service?.name || booking?.category?.name || 'Booking',
    category: booking?.category?.name || '',
    categoryId: booking?.categoryId ?? booking?.category?.id ?? null,
    serviceId: service?.id ?? booking?.services?.[0]?.serviceId ?? null,
    date: booking?.dateAndTime || attrs.event_date,
    time: attrs.event_time || '',
    amount: Number(booking?.totalPrice ?? 0),
    subtotal: Number(booking?.subTotal ?? booking?.subtotal ?? 0),
    discount: Number(booking?.discountAmount ?? 0),
    platformFee: Number(booking?.platformFee ?? 0),
    tax: Number(booking?.taxAmount ?? 0),
    venue: cleanLocationText(location?.address || provider?.address || '') || '',
    city: cleanLocationText(location?.city || provider?.city || '') || '',
    latitude: Number(location?.latitude) || Number(provider?.latitude) || null,
    longitude: Number(location?.longitude) || Number(provider?.longitude) || null,
    providerName: provider?.name || '',
    providerPhone: provider?.phone || '',
    providerId: provider?.id ?? booking?.providerId ?? null,
    image: service?.thumbnail || provider?.logo || provider?.cover || '',
    lines,
    ticketCount,
    createdAt: booking?.createdAt || null,
    durationMin: Number(booking?.serviceDuration) || null,
    cancelReason: booking?.cancellationReason || booking?.cancelReason || null,
    raw: booking,
  };
};

/* --------------------------------------------------------------- calendar */

const icsEscape = (text = '') =>
  String(text).replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');

const icsUtc = (date) =>
  `${date.getUTCFullYear()}${String(date.getUTCMonth() + 1).padStart(2, '0')}${String(date.getUTCDate()).padStart(2, '0')}` +
  `T${String(date.getUTCHours()).padStart(2, '0')}${String(date.getUTCMinutes()).padStart(2, '0')}00Z`;

// IST has no DST, so the offset is fixed.
const IST_OFFSET_MIN = 330;

/**
 * An .ics for a booking, or null when it has no usable start time.
 *
 * The start is the booking's wall-clock time (see parseBookingTime), which is
 * IST, converted to real UTC so every calendar app places it correctly
 * regardless of the phone's own time zone.
 */
export const buildBookingIcs = (booking, { url } = {}) => {
  const wall = parseBookingTime(booking?.date);
  if (!wall) return null;
  const startUtc = new Date(
    Date.UTC(wall.getFullYear(), wall.getMonth(), wall.getDate(), wall.getHours(), wall.getMinutes()) -
      IST_OFFSET_MIN * 60000
  );
  const endUtc = new Date(startUtc.getTime() + (booking.durationMin || 120) * 60000);
  const where = [booking.providerName, booking.venue, booking.city].filter(Boolean).join(', ');
  const details = [
    booking.code ? `Booking code: ${booking.code}` : `Booking #${booking.id}`,
    booking.ticketCount ? `${booking.ticketCount} ticket${booking.ticketCount === 1 ? '' : 's'}` : null,
    'Show your QR code with a valid photo ID at the entrance.',
    url || null,
  ]
    .filter(Boolean)
    .join('\n');

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//LocalKonnect//Bookings//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:booking-${booking.id}@localkonnect.com`,
    `DTSTAMP:${icsUtc(new Date())}`,
    `DTSTART:${icsUtc(startUtc)}`,
    `DTEND:${icsUtc(endUtc)}`,
    `SUMMARY:${icsEscape(booking.title)}`,
    where ? `LOCATION:${icsEscape(where)}` : null,
    `DESCRIPTION:${icsEscape(details)}`,
    url ? `URL:${url}` : null,
    'END:VEVENT',
    'END:VCALENDAR',
  ]
    .filter(Boolean)
    .join('\r\n');
};

/** Start of the booked slot, for deciding whether "Add to calendar" still makes sense. */
export const bookingStartsInFuture = (booking) => {
  const wall = parseBookingTime(booking?.date);
  return !!wall && wall.getTime() > Date.now();
};

/** Save an .ics — iOS opens it straight into Calendar, Android hands it to the installed calendar app. */
export const downloadIcs = (ics, title = 'event') => {
  const href = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = href;
  link.download = `${String(title).replace(/[^\w\- ]+/g, '').trim() || 'event'}.ics`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
};

/**
 * "2026-10-17" + "7:00 pm" (or "7:00 pm – 10:00 pm") → "2026-10-17 19:00",
 * the same wall-clock shape parseBookingTime reads. Null if either is missing.
 */
export const wallClockFrom = (isoDay, timeLabel) => {
  if (!isoDay || !/^\d{4}-\d{2}-\d{2}$/.test(String(isoDay))) return null;
  const m = String(timeLabel || '').match(/(\d{1,2}):(\d{2})\s*([ap]m)/i);
  if (!m) return null;
  let hour = Number(m[1]) % 12;
  if (m[3].toLowerCase() === 'pm') hour += 12;
  return `${isoDay} ${String(hour).padStart(2, '0')}:${m[2]}`;
};
